import React from 'react';
import { Box } from '@heycater/design-system';
import { Typography } from '@material-ui/core';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import NavbarBase from 'qualification/components/NavbarBase';
import { useQualification } from 'qualification/context';
import { QUALIFICATION_STATUS } from 'qualification/context/model';
import { useBreakpointDown } from 'qualification/hooks/useBreakpoints';
import {
  formatRating,
  getRating,
} from 'shared/services/trustedShops/handleRatings';
import { Ratings } from 'shared/services/trustedShops/model';
import TrustedShopsLogo from 'shared/svg/TrustedShopsLogo';
import TrustedShopsStars from 'shared/svg/TrustedShopsStars';

const PROGRESS_BAR = {
  HEIGHT: {
    DESKTOP: 10,
    MOBILE: 8,
  },
};

interface Props {
  ratings: Ratings;
}

function SurveyNavbar({ ratings }: Props) {
  const { state } = useQualification();

  const isMobile = useBreakpointDown('xs');
  const isSmallScreen = useBreakpointDown('sm');

  const { t } = useTranslation('common');

  if (
    state.status !== QUALIFICATION_STATUS.answering &&
    state.status !== QUALIFICATION_STATUS.answered
  ) {
    return null;
  }

  const percentageToCompletion =
    state.status === QUALIFICATION_STATUS.answering
      ? state.qualification.progressPercentage
      : 100;

  return (
    <React.Fragment>
      <NavBarWrapper>
        {isSmallScreen && (
          <NavbarEyebrow>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexWrap="nowrap"
            >
              <TrustedShopsBox>
                <Stars>
                  <TrustedShopsStars />
                </Stars>
                <Text>
                  <strong>{formatRating(getRating(ratings))}</strong>
                  <RatingText>&nbsp;{t('trustedShops.ratingText')}</RatingText>
                </Text>
                <TSLogoBox>
                  <TrustedShopsLogo />
                </TSLogoBox>
              </TrustedShopsBox>
            </Box>
          </NavbarEyebrow>
        )}
        <NavbarBase ratings={ratings} showLogin logoHref="/" />
      </NavBarWrapper>

      {isMobile ? (
        <ProgressBarTrack>
          <StyledProgressBar $width={`${percentageToCompletion}%`} />
        </ProgressBarTrack>
      ) : (
        <Box display="flex" alignItems="center" mt={3}>
          <Box ml="auto" pl={4} flexShrink={0} pr={4}>
            <Link href="/" passHref>
              <TypographyBackLink as="a" id="close-funnel-btn">
                {t('labels.cancel')}
              </TypographyBackLink>
            </Link>
          </Box>

          <ProgressBarTrack>
            <StyledProgressBar $width={`${percentageToCompletion}%`} />
          </ProgressBarTrack>
          <Box mr="auto" pr={4} pl={4} flexShrink={0}>
            <Typography>
              {percentageToCompletion}% {t('labels.done').toLowerCase()}
            </Typography>
          </Box>
        </Box>
      )}
    </React.Fragment>
  );
}

const NavBarWrapper = styled.div`
  border-bottom: 1px solid ${(props) => props.theme.palette.grey[250]};
`;

const TypographyBackLink = styled(Typography)`
  text-decoration: underline;
  font-weight: 500;
  color: ${(props) => props.theme.palette.text.primary};
`;

const ProgressBarTrack = styled.div`
  height: ${PROGRESS_BAR.HEIGHT.DESKTOP}px;
  background: ${(props) => props.theme.palette.grey[250]};
  position: relative;
  max-width: 740px;
  width: 100%;
  border-radius: 10px;
  overflow: hidden;

  ${(props) => props.theme.breakpoints.down('xs')} {
    height: ${PROGRESS_BAR.HEIGHT.MOBILE}px;
    border-radius: 0px;
  }
`;

const StyledProgressBar = styled.div<{ $width: string }>`
  position: absolute;
  height: ${PROGRESS_BAR.HEIGHT.DESKTOP}px;
  background: ${(props) => props.theme.palette.primary.main};
  transition: ${(props) =>
    props.theme.transitions.create('width', {
      duration: '250ms',
    })};
  width: ${(props) => (props.$width ? props.$width : '0')};
`;

const NavbarEyebrow = styled.section`
  padding: ${(props) => props.theme.spacing(1.25)}px;
  background-color: ${(props) => props.theme.palette.grey[800]};
  color: white;
  font-size: 12px;
`;

const TrustedShopsBox = styled(Box)`
  display: flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  color: white;
`;

const Stars = styled(Box)`
  height: 17px;
  width: 100%;
  min-width: 70px;
`;

const RatingText = styled.span``;

const TSLogoBox = styled(Box)`
  display: flex;
  align-items: center;
  min-height: 12px;
`;

const Text = styled.span`
  flex-shrink: 0;
`;

export default SurveyNavbar;
